/**
 * RunDetailActions — detail level switch and run actions. Demo page: /ui-elements/tabs (segmented) and /ui-elements/buttons.
 * Re-run and Cancel call POST /agents/workflows/runs/:runId/actions (CanManageWorkflows); the Full I/O level is also
 * restricted to that policy by the debugger route, so the option only shows to people who hold it.
 */
import React from 'react';
import { RotateCw, Square } from 'lucide-react';
import { Can } from '../../../components/auth/Can.js';
import { Button } from '../../../components/ui/Button.js';
import { Tabs } from '../../../components/ui/Tabs.js';
import { usePolicy } from '../../../hooks/usePolicy.js';
import type { DebugPayload, DetailLevel } from './run-detail-types.js';

interface RunDetailActionsProps {
  payload: DebugPayload;
  detail: DetailLevel;
  onDetailChange: (level: DetailLevel) => void;
  onAction: (action: string) => void;
  actionPending: boolean;
}

const LEVELS: { id: DetailLevel; label: string }[] = [
  { id: 'summary', label: 'Summary' },
  { id: 'debug', label: 'Debug' },
  { id: 'full', label: 'Full I/O' },
];

export const RunDetailActions: React.FC<RunDetailActionsProps> = ({
  payload,
  detail,
  onDetailChange,
  onAction,
  actionPending,
}) => {
  const { actionAvailability } = payload;
  const canFull = usePolicy('CanManageWorkflows');
  const canCancel = actionAvailability['cancel'] ?? false;
  const canRetry = actionAvailability['retry_run'] ?? actionAvailability['retryRun'] ?? false;

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <Tabs
        variant="segmented"
        aria-label="Detail level"
        tabs={LEVELS.filter((l) => l.id !== 'full' || canFull)}
        activeTab={detail}
        onChange={(id) => onDetailChange(id as DetailLevel)}
      />
      <Can policy="CanManageWorkflows">
        {canRetry && (
          <Button variant="secondary" size="sm" loading={actionPending} onClick={() => onAction('retry_run')}>
            <RotateCw size={13} aria-hidden="true" />
            Re-run
          </Button>
        )}
        {canCancel && (
          <Button variant="secondary" size="sm" loading={actionPending} onClick={() => onAction('cancel')}>
            <Square size={13} aria-hidden="true" />
            Cancel run
          </Button>
        )}
      </Can>
    </div>
  );
};
